import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Role } from '../../types/role';
import { api } from '../../services/api';
import { useToast } from '../../components/UI/ToastProvider';

const initialForm: Omit<Role, 'id' | 'isSystem'> = {
  code: '',
  name: '',
  description: '',
  type: 'user',
  active: true,
  color: '#000000',
  labelColor: '#FFFFFF'
};

const typeOptions = [
  { value: 'admin', label: 'Administrador' },
  { value: 'manager', label: 'Gerente' },
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Visualizador' },
  { value: 'user', label: 'Usuário' },
  { value: 'anonymous', label: 'Anônimo' }
];

export const RoleEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<typeof initialForm>(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSystem, setIsSystem] = useState(false);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const isNew = !id;

  useEffect(() => {
    if (!isNew) {
      fetchRole();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchRole = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/roles/${id}`);
      setForm({
        code: response.data.code,
        name: response.data.name,
        description: response.data.description || '',
        type: response.data.type,
        active: response.data.active,
        color: response.data.color || '#000000',
        labelColor: response.data.labelColor || '#FFFFFF'
      });
      setIsSystem(response.data.isSystem);
    } catch (error: any) {
      let msg = 'Erro ao carregar dados do perfil.';
      if (error.response?.data?.message) {
        msg = error.response.data.message;
      }
      addToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      setForm(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.code?.trim()) newErrors.code = 'Código é obrigatório';
    if (!form.name?.trim()) newErrors.name = 'Nome é obrigatório';
    if (!form.type) newErrors.type = 'Tipo é obrigatório';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (isNew) {
        await api.post('/roles', form);
        addToast('Perfil criado com sucesso!', 'success');
      } else {
        await api.put(`/roles/${id}`, form);
        addToast('Perfil atualizado com sucesso!', 'success');
      }
      navigate('/admin/roles');
    } catch (error: any) {
      let msg = isNew ? 'Erro ao criar perfil.' : 'Erro ao atualizar perfil.';
      if (error.response?.data?.message) {
        msg = error.response.data.message;
      }
      addToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="pt-8 pb-8 px-5 w-full" style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          {isNew ? 'Novo Perfil' : 'Editar Perfil'}
          {isSystem && (
            <span className="ml-2 bg-gray-100 text-gray-600 text-sm font-medium px-2 py-1 rounded">
              Perfil do Sistema
            </span>
          )}
        </h1>
        <button
          type="button"
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
          onClick={() => navigate('/admin/roles')}
          title="Voltar para a lista"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white rounded-lg shadow p-6">
        <div className="col-span-1 md:col-span-2 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <label className="block font-medium mb-1">
              Código <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="code"
              value={form.code}
              onChange={handleInputChange}
              className="w-full border rounded px-3 py-2"
              maxLength={50}
              required
              disabled={isSystem}
              placeholder="Ex: admin, editor, etc"
            />
            {errors.code && <span className="text-red-600 text-sm">{errors.code}</span>}
          </div>
          <div className="flex items-center mt-2 md:mt-6">
            <label className="block font-medium mb-1 mr-2" htmlFor="active">Ativo</label>
            <input
              id="active"
              name="active"
              type="checkbox"
              checked={form.active}
              onChange={handleInputChange}
              className="h-5 w-5 cursor-pointer"
              disabled={isSystem}
            />
          </div>
        </div>

        <div>
          <label className="block font-medium mb-1">
            Nome <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleInputChange}
            className="w-full border rounded px-3 py-2"
            maxLength={100}
            required
            placeholder="Ex: Administrador, Editor, etc"
          />
          {errors.name && <span className="text-red-600 text-sm">{errors.name}</span>}
        </div>

        <div>
          <label className="block font-medium mb-1">
            Tipo <span className="text-red-500">*</span>
          </label>
          <select
            name="type"
            value={form.type}
            onChange={handleInputChange}
            className="w-full border rounded px-3 py-2"
            required
            disabled={isSystem}
          >
            {typeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.type && <span className="text-red-600 text-sm">{errors.type}</span>}
        </div>

        <div className="col-span-1 md:col-span-2">
          <label className="block font-medium mb-1">Descrição</label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleInputChange}
            className="w-full border rounded px-3 py-2"
            rows={3}
            maxLength={255}
            placeholder="Descreva as principais características deste perfil"
          />
        </div>

        <div>
          <label className="block font-medium mb-1">Cor do Perfil</label>
          <input
            type="color"
            name="color"
            value={form.color && /^#[0-9A-Fa-f]{6}$/.test(form.color) ? form.color : '#000000'}
            onChange={handleInputChange}
            className="w-16 h-10 p-0 border rounded cursor-pointer"
            disabled={isSystem}
          />
        </div>

        <div>
          <label className="block font-medium mb-1">Cor do Texto</label>
          <input
            type="color"
            name="labelColor"
            value={form.labelColor && /^#[0-9A-Fa-f]{6}$/.test(form.labelColor) ? form.labelColor : '#FFFFFF'}
            onChange={handleInputChange}
            className="w-16 h-10 p-0 border rounded cursor-pointer"
            disabled={isSystem}
          />
        </div>

        <div className="flex items-end h-full mt-6 md:mt-0 justify-end gap-2 col-span-1 md:col-span-2">
          <button
            type="button"
            className="px-4 py-2 bg-gray-200 rounded cursor-pointer"
            onClick={() => navigate('/admin/roles')}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer"
            disabled={saving || isSystem}
          >
            {saving ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Salvando...</span>
              </div>
            ) : (
              'Salvar'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}; 